import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import useStreamSharing from './useStreamSharing';

/**
 * Smart stream manager with connection pooling, adaptive loading, and stream sharing
 */
const useStreamManager = () => {
  // Stream sharing integration
  const streamSharing = useStreamSharing();
  const [activeStreams, setActiveStreams] = useState(new Map());
  const [streamPool, setStreamPool] = useState(new Map());
  const [loadingStreams, setLoadingStreams] = useState(new Set());
  const cleanupTimers = useRef(new Map());
  const maxConcurrentStreams = useRef(9); // Default 3x3 grid, will be updated from server config
  const streamQueue = useRef([]);

  // Fetch server config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await api.get('/streams/config');
        if (response.data?.data?.maxConcurrentStreams) {
          maxConcurrentStreams.current = response.data.data.maxConcurrentStreams;
          console.log(`Stream manager: Max concurrent streams set to ${maxConcurrentStreams.current} from server config`);
        }
      } catch (error) {
        console.warn('Failed to fetch stream config, using default:', error);
      }
    };
    fetchConfig();
  }, []);

  // Cleanup stream with delay to allow for quick re-use
  const scheduleStreamCleanup = useCallback((cameraId, delay = 30000) => {
    // Clear existing cleanup timer
    if (cleanupTimers.current.has(cameraId)) {
      clearTimeout(cleanupTimers.current.get(cameraId));
    }

    const timer = setTimeout(() => {
      setActiveStreams(prev => {
        const newMap = new Map(prev);
        const stream = newMap.get(cameraId);
        if (stream && stream.hls) {
          stream.hls.destroy();
        }
        newMap.delete(cameraId);
        return newMap;
      });

      setStreamPool(prev => {
        const newMap = new Map(prev);
        newMap.delete(cameraId);
        return newMap;
      });

      cleanupTimers.current.delete(cameraId);
      
      // Process queue if we have capacity
      processStreamQueue();
    }, delay);

    cleanupTimers.current.set(cameraId, timer);
  }, []);

  // Cancel scheduled cleanup (camera became visible again)
  const cancelStreamCleanup = useCallback((cameraId) => {
    if (cleanupTimers.current.has(cameraId)) {
      clearTimeout(cleanupTimers.current.get(cameraId));
      cleanupTimers.current.delete(cameraId);
    }
  }, []);

  // Process stream queue based on priority
  const processStreamQueue = useCallback(() => {
    if (streamQueue.current.length === 0) return;
    
    const currentActiveCount = activeStreams.size;
    const availableSlots = maxConcurrentStreams.current - currentActiveCount;
    
    if (availableSlots > 0) {
      const toProcess = streamQueue.current.splice(0, availableSlots);
      toProcess.forEach(({ cameraId, priority, callback }) => {
        callback();
      });
    }
  }, [activeStreams.size]);

  // Request stream with priority queue
  const requestStream = useCallback((cameraId, priority = 0, callback) => {
    // Cancel any scheduled cleanup
    cancelStreamCleanup(cameraId);

    // If stream already exists, reuse it
    if (activeStreams.has(cameraId)) {
      return activeStreams.get(cameraId);
    }

    // If we have capacity, start immediately
    if (activeStreams.size < maxConcurrentStreams.current) {
      callback();
    } else {
      // Add to queue with priority
      streamQueue.current.push({ cameraId, priority, callback });
      streamQueue.current.sort((a, b) => b.priority - a.priority);
    }
  }, [activeStreams, cancelStreamCleanup]);

  // Release stream (schedule for cleanup) with sharing integration
  const releaseStream = useCallback(async (cameraId, immediate = false) => {
    // Leave stream sharing session
    await streamSharing.leaveStreamSession(cameraId);

    if (immediate) {
      // Immediate cleanup
      const stream = activeStreams.get(cameraId);
      if (stream && stream.hls) {
        stream.hls.destroy();
      }
      
      setActiveStreams(prev => {
        const newMap = new Map(prev);
        newMap.delete(cameraId);
        return newMap;
      });

      setStreamPool(prev => {
        const newMap = new Map(prev);
        newMap.delete(cameraId);
        return newMap;
      });

      processStreamQueue();
    } else {
      // Delayed cleanup
      scheduleStreamCleanup(cameraId);
    }
  }, [activeStreams, scheduleStreamCleanup, processStreamQueue, streamSharing]);

  // Register active stream with sharing integration
  const registerStream = useCallback(async (cameraId, streamData) => {
    setActiveStreams(prev => new Map(prev).set(cameraId, streamData));
    setStreamPool(prev => new Map(prev).set(cameraId, streamData));
    
    // Remove from loading
    setLoadingStreams(prev => {
      const newSet = new Set(prev);
      newSet.delete(cameraId);
      return newSet;
    });

    // Join stream sharing session
    await streamSharing.joinStreamSession(cameraId, {
      userId: streamData.userId || 'anonymous',
      streamType: streamData.type || 'live',
      quality: streamData.quality || 'auto'
    });
  }, [streamSharing]);

  // Mark stream as loading
  const markLoading = useCallback((cameraId) => {
    setLoadingStreams(prev => new Set(prev).add(cameraId));
  }, []);

  // Check if stream is active
  const isStreamActive = useCallback((cameraId) => {
    return activeStreams.has(cameraId);
  }, [activeStreams]);

  // Check if stream is loading
  const isStreamLoading = useCallback((cameraId) => {
    return loadingStreams.has(cameraId);
  }, [loadingStreams]);

  // Get stream instance
  const getStream = useCallback((cameraId) => {
    return activeStreams.get(cameraId);
  }, [activeStreams]);

  // Adjust max concurrent streams based on device capabilities
  useEffect(() => {
    const updateCapacity = () => {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const memoryInfo = navigator.deviceMemory || 4; // Default to 4GB if not available
      
      let capacity = 6; // Default
      
      if (connection) {
        const effectiveType = connection.effectiveType;
        switch (effectiveType) {
          case 'slow-2g':
            capacity = 1;
            break;
          case '2g':
            capacity = 2;
            break;
          case '3g':
            capacity = 4;
            break;
          case '4g':
            capacity = Math.min(8, memoryInfo * 2);
            break;
        }
      }
      
      // Adjust based on memory
      if (memoryInfo < 4) {
        capacity = Math.min(capacity, 3);
      } else if (memoryInfo >= 8) {
        capacity = Math.min(capacity + 2, 10);
      }
      
      maxConcurrentStreams.current = capacity;
      console.log(`Stream manager capacity set to: ${capacity} concurrent streams`);
    };

    updateCapacity();
    
    // Listen for connection changes
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      connection.addEventListener('change', updateCapacity);
      return () => connection.removeEventListener('change', updateCapacity);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear all timers
      cleanupTimers.current.forEach(timer => clearTimeout(timer));
      cleanupTimers.current.clear();
      
      // Destroy all active streams
      activeStreams.forEach(stream => {
        if (stream.hls) {
          stream.hls.destroy();
        }
      });
    };
  }, []);

  return {
    requestStream,
    releaseStream,
    registerStream,
    markLoading,
    isStreamActive,
    isStreamLoading,
    getStream,
    activeStreamCount: activeStreams.size,
    maxConcurrentStreams: maxConcurrentStreams.current,
    queueLength: streamQueue.current.length,
    
    // Stream sharing integration
    streamSharing,
    getViewerCount: streamSharing.getViewerCount,
    getSharingEfficiency: streamSharing.getSharingEfficiency,
    sessionStats: streamSharing.sessionStats
  };
};

export default useStreamManager;